import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { isIntegrator, type Integrator } from '@designdeck/shared';
import { MotionConfig } from 'motion/react';
import { Toaster } from 'react-hot-toast';
import { startTransition, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DesignsPage,
  BulkCreationPage,
  CreatorPage,
  LoginPage,
  ShopWizardPage,
  DirectSalePage,
  TryDirectSalePage,
  OnboardingWizardPage,
  AdminDashboardPage,
  AdminProductsPage,
  AdminProductRoute,
  FrameReviewPage,
  AdminDecorationMethodsPage,
  AdminUsersPage,
  AdminDesignsPage,
  AdminDesignDetailPage,
  ReviewPage,
  ConnectionsPage,
  ShopPreviewPage,
  ImpersonatePage,
} from '@/pages';
import {
  ProtectedRoute,
  AdminRoute,
  RoleDispatch,
  IframeNoticeScreen,
} from '@/components/auth';
import { SellerShell } from '@/components/layout/SellerShell';
import { ImpersonationBanner } from '@/components/layout/ImpersonationBanner';
import { ImpersonationEndedScreen } from '@/components/impersonation/ImpersonationEndedScreen';
import { impersonationEndReason } from '@/lib/impersonation-session';
import { AdminShell } from '@/components/layout/AdminShell';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { RouteErrorFallback } from '@/components/ui/RouteErrorFallback';
import { HostHomeRedirect } from '@/components/designs/DesignEntryChoice';
import { embedLayout } from '@/lib/embed-layout';
import { observeEmbedHeight } from '@/lib/embed-messages';
import { creatorEntryPath, startsNewDesign } from '@/lib/creator-entry';
import { useShopLanguageRedirect } from '@/lib/keep-shop-language';
import { LoadingState } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { useBrandingSync } from '@/hooks/useBrandingSync';

interface IframeAuth {
  userId: string;
  timestamp: string;
  signature: string;
  integrator: Integrator;
}

/** True when the URL announces an embedded start, whatever else it carries. */
function isIframeStart(): boolean {
  return new URLSearchParams(window.location.search).get('isIframe') === 'true';
}

/**
 * Resolves the signed iframe auth params from the URL:
 * `?key=<DesignDeck userId>&ts=<unix seconds>&sig=<hmac hex>&integrator=<host>&isIframe=true`.
 * There is no unsigned path any more: a key without ts/sig, or a start that
 * names no known integrator, fails closed.
 */
function resolveIframeAuth(): IframeAuth | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get('isIframe') !== 'true') {
    return null;
  }

  const key = params.get('key');
  const timestamp = params.get('ts');
  const signature = params.get('sig');
  const integrator = params.get('integrator');
  if (!key || !timestamp || !signature || !isIntegrator(integrator)) {
    return null;
  }

  return { userId: key, timestamp, signature, integrator };
}

/**
 * Parameters an embedded start consumes at boot: the auto-login credentials
 * and the postMessage channel, all read before this redirect runs and none
 * of them needed again. They are stripped; everything else the embedder sent
 * survives.
 *
 * A denylist on purpose. The allowlist it replaces forwarded only `product`
 * and `color`, so `?sku=`, `?step=` and `?editProduct=` were dropped without
 * a word and the guided entry had to be added to it by hand. Anything the
 * Creator learns to read later works here without a change; only a new
 * boot-time parameter belongs in this list.
 */
const IFRAME_BOOT_PARAMS = [
  'isIframe',
  'key',
  'ts',
  'sig',
  'integrator',
  'channelName',
] as const;

function buildCreatorPathFromSearch(): string {
  const next = new URLSearchParams(window.location.search);
  for (const param of IFRAME_BOOT_PARAMS) {
    next.delete(param);
  }

  const basePath = creatorEntryPath(window.location.pathname);
  const query = next.toString();
  if (query) {
    return `${basePath}?${query}`;
  }

  // `new=1` says "start empty", which a path naming a design contradicts.
  // CreatorPage reads the flag only on a path without a design id, so this
  // is about the URL the seller ends up looking at, not about behaviour.
  // Bulk Creation is no creator path and takes no flag either.
  return startsNewDesign(basePath) ? `${basePath}?new=1` : basePath;
}

/**
 * `/designs`: the design overview, or the guided entry where the host frames
 * the app and owns the navigation (lib/embed-layout.ts, `hostChrome`).
 */
function SellerHome() {
  return embedLayout().hostChrome ? <HostHomeRedirect /> : <DesignsPage />;
}

/**
 * Redirects a splat route to another mount point, keeping the rest of the
 * path. `Navigate` alone drops the splat, so `/onboarding/customer` would
 * land on `/admin/onboarding` and lose its step.
 */
function RedirectWithSplat({ to }: { to: string }) {
  const rest = useParams()['*'] ?? '';
  const { search } = useLocation();
  return <Navigate to={`${rest ? `${to}/${rest}` : to}${search}`} replace />;
}

/**
 * The app as one tab sees it. An impersonation tab whose session is over
 * (lib/impersonation-session.ts) renders only the end screen, whatever the
 * route: no guard, no request, no way to /login inside a tab that must stay
 * apart from the admin's session. A running impersonation tab gets the
 * banner above every route, the Creator included; `ImpersonationBanner`
 * renders nothing in an ordinary tab.
 */
export function AppRoutes() {
  const endReason = impersonationEndReason();
  if (endReason) {
    return <ImpersonationEndedScreen reason={endReason} />;
  }

  return (
    <>
      <ImpersonationBanner />
      <AppRouteTree />
    </>
  );
}

/**
 * Renders routes OR a full-screen overlay while iframe auto-login runs.
 * A failed auto-login keeps the overlay in an error state instead of
 * rendering routes at `/?key=…`.
 */
function AppRouteTree() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const languageRedirect = useShopLanguageRedirect();
  const {
    loginIframe,
    resumeIframeSession,
    setIsIframe,
    setIframeChannelName,
    iframeLoginFailed,
    setIframeLoginFailed,
    syncIframeFlagFromBoot,
  } = useAuthStore();
  useBrandingSync();

  const [iframeLoginPending, setIframeLoginPending] = useState(isIframeStart);

  useEffect(() => {
    syncIframeFlagFromBoot();

    if (!isIframeStart()) {
      return;
    }

    const auth = resolveIframeAuth();
    if (!auth) {
      // Embedded start without usable credentials (no key, or a
      // marketplace key without ts/sig): fail closed, never show /login.
      setIframeLoginFailed(true);
      setIframeLoginPending(false);
      return;
    }

    // Mark the session as embedded before the first request goes out. The
    // API client's 401 handler decides by this flag whether a lost session
    // becomes state (notice inside the iframe) or a reload into /login; on a
    // first embed the flag is still false, and a rejected signature would
    // otherwise yank the shop's iframe onto DesignDeck's login page.
    setIsIframe(true);

    const params = new URLSearchParams(window.location.search);
    const channelName = params.get('channelName');
    const creatorPath = buildCreatorPathFromSearch();

    const completeLogin = () => {
      setIsIframe(true);
      if (channelName) {
        // A channel key from the shop, not a channel id: the publish step
        // matches it against the resolved channels and preselects from there.
        setIframeChannelName(channelName);
      }
      // Router navigations are transitions; a plain state update would
      // commit first, render the routes at `/` and let RoleDispatch's
      // redirect race the creator path. One transition commits both.
      startTransition(() => {
        setIframeLoginPending(false);
        navigate(creatorPath, { replace: true });
      });
    };

    const failLogin = () => {
      setIframeLoginFailed(true);
      setIframeLoginPending(false);
    };

    loginIframe(auth.userId, auth.timestamp, auth.signature, auth.integrator)
      .then(completeLogin)
      .catch(() =>
        // The signature is stale after the shop's 300 s window (tab left
        // open, iframe reloaded). A session this origin already holds for
        // the same user carries on; anything else fails closed.
        resumeIframeSession(auth.userId).then(completeLogin).catch(failLogin)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (iframeLoginFailed) {
    return <IframeNoticeScreen reason="loginFailed" />;
  }

  if (iframeLoginPending) {
    return <LoadingState fullscreen label={t('common.loading')} />;
  }

  // Fix the URL before any route mounts: a route redirecting on mount would
  // otherwise race the correction (see lib/keep-shop-language.ts).
  if (languageRedirect) {
    return <Navigate to={languageRedirect} replace />;
  }

  return (
    <Routes>
      {/* Role dispatch: `/` verifies the session and forwards by role */}
      <Route path="/" element={<RoleDispatch />} />

      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/shop-wizard" element={<ShopWizardPage />} />

      {/* Direct Sale: end customers of an embedding shop. No DesignDeck
          account — access is proven by the shop's signature on the URL and
          verified server-side on every API call, so no ProtectedRoute. */}
      <Route path="/direct-sale" element={<DirectSalePage />} />
      {/* The seller's shareable test link: trades its token for a fresh
          signed entry and becomes the bare configurator. */}
      <Route path="/try/:token" element={<TryDirectSalePage />} />

      {/* Impersonation exchange: the tab an admin opens from the user list
          lands here with a one-time code in the fragment. No guard — the
          tab has no session yet, and a guard would use the admin's. */}
      <Route path="/impersonate" element={<ImpersonatePage />} />

      {/* Seller branch: one guard as layout route, the seller shell inside
          it. */}
      <Route element={<ProtectedRoute />}>
        <Route element={<SellerShell />}>
          <Route path="/designs" element={<SellerHome />} />
          {/* Bulk Creation: many listings from one reference. A static child
              of /designs, so it can never be read as a design id. */}
          <Route path="/designs/bulk" element={<BulkCreationPage />} />
          <Route path="/review" element={<ReviewPage />} />
          <Route path="/connections" element={<ConnectionsPage />} />
          {/* The seller's own look at Direct Sale, embedded like in a shop. */}
          <Route path="/shop-preview" element={<ShopPreviewPage />} />
        </Route>
      </Route>

      {/* Protected: Design Creator (keeps its per-route wrapper) */}
      <Route
        path="/creator"
        element={
          <ProtectedRoute>
            <CreatorPage />
          </ProtectedRoute>
        }
      />
      {/* Guided entry: motif first, products second. A route of its own so
          every way into the Creator is addressable, and static-over-dynamic
          ranking keeps it from being read as a design id. */}
      <Route
        path="/creator/guided"
        element={
          <ProtectedRoute>
            <CreatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator/:designId"
        element={
          <ProtectedRoute>
            <CreatorPage />
          </ProtectedRoute>
        }
      />

      {/* Admin branch: one guard as layout route, the admin shell beneath it
          frames every page. */}
      <Route path="/admin" element={<AdminRoute />}>
        <Route element={<AdminShell />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="products" element={<AdminProductsPage />} />
          <Route path="products/frame-review" element={<FrameReviewPage />} />
          <Route path="products/:productId" element={<AdminProductRoute />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="designs" element={<AdminDesignsPage />} />
          <Route path="designs/:designId" element={<AdminDesignDetailPage />} />
          <Route
            path="decoration-methods"
            element={<AdminDecorationMethodsPage />}
          />
          <Route path="onboarding/*" element={<OnboardingWizardPage />} />
        </Route>
      </Route>

      {/* Legacy mount of the onboarding wizard */}
      <Route
        path="/onboarding/*"
        element={<RedirectWithSplat to="/admin/onboarding" />}
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Data router (createBrowserRouter) so pages can register navigation
 * blockers (useBlocker) for unsaved-changes protection. AppRoutes stays
 * a descendant-<Routes> component so the iframe auto-login overlay can
 * gate the whole tree.
 */
const router = createBrowserRouter([
  { path: '*', element: <AppRoutes />, errorElement: <RouteErrorFallback /> },
]);

function App() {
  // Polaris embed only (lib/embed-messages.ts): the host sizes the iframe.
  useEffect(() => observeEmbedHeight(), []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* Every motion animation in the app honours the viewer's
            reduced-motion setting from here. */}
        <MotionConfig reducedMotion="user">
          <RouterProvider router={router} />
        </MotionConfig>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--paper-2)',
              color: 'var(--ink)',
              boxShadow: 'inset 0 0 0 1px var(--line)',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
            },
            success: {
              iconTheme: {
                primary: 'var(--success)',
                secondary: 'var(--paper)',
              },
            },
            error: {
              iconTheme: {
                primary: 'var(--danger)',
                secondary: 'var(--paper)',
              },
            },
          }}
        />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
